import React, { useEffect, useRef, useState } from "react";
import { club } from "../mock";
import { extractYouTubeId, loadYT } from "../lib/video";

const HOST_CLS =
  "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full [&>iframe]:absolute [&>iframe]:inset-0 [&>iframe]:w-full [&>iframe]:h-full";

// A YouTube clip that plays its loop windows (mm:ss) in order. When it is the only
// clip it wraps its windows forever; otherwise it calls onDone after the last window
// (or on natural end when it has no windows) so the playlist advances to the next clip.
function YouTubeClip({ videoId, loops, single, onReady, onSeek, onDone, onError }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const loopIdxRef = useRef(0);
  const segs = Array.isArray(loops) && loops.length ? loops : null;

  useEffect(() => {
    let mounted = true;
    let interval;
    loopIdxRef.current = 0;
    const firstStart = segs ? segs[0].start : 0;
    const host = hostRef.current;
    if (!host) return undefined;
    // Mount the YT player on a manually-created child node so the YouTube API can replace it
    // with its <iframe> without corrupting React's DOM tree (prevents the
    // "removeChild: node is not a child of this node" crash on unmount).
    const mountNode = document.createElement("div");
    host.appendChild(mountNode);

    loadYT().then((YT) => {
      if (!mounted) return;
      playerRef.current = new YT.Player(mountNode, {
        videoId,
        playerVars: {
          autoplay: 1, mute: 1, controls: 0, playsinline: 1, modestbranding: 1,
          rel: 0, showinfo: 0, disablekb: 1, iv_load_policy: 3, fs: 0,
          loop: (!segs && single) ? 1 : 0,
          playlist: (!segs && single) ? videoId : undefined,
          start: firstStart,
        },
        events: {
          onReady: (e) => { e.target.mute(); if (segs) e.target.seekTo(firstStart, true); e.target.playVideo(); },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING && onReady) onReady();
            if (e.data === YT.PlayerState.ENDED && !segs && !single && onDone) onDone();
          },
          onError: () => { if (onError) onError(); },
        },
      });

      if (segs) {
        interval = setInterval(() => {
          const p = playerRef.current;
          if (!p || !p.getCurrentTime) return;
          const t = p.getCurrentTime();
          const seg = segs[loopIdxRef.current];
          if (t >= seg.end || t < seg.start - 3) {
            const isLast = loopIdxRef.current >= segs.length - 1;
            if (isLast && !single) { if (onDone) onDone(); return; }
            const next = single ? (loopIdxRef.current + 1) % segs.length : loopIdxRef.current + 1;
            loopIdxRef.current = next;
            if (onSeek) onSeek();
            p.seekTo(segs[next].start, true);
          }
        }, 250);
      }
    });

    return () => {
      mounted = false;
      clearInterval(interval);
      const p = playerRef.current;
      try { if (p && typeof p.destroy === "function") p.destroy(); } catch (e) { console.debug("YT player destroy on unmount (expected)", e); }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, JSON.stringify(loops), single]);

  return <div ref={hostRef} className={HOST_CLS} />;
}

// Cycles through background clips (YouTube w/ optional loop windows, or MP4), looping back
// to the first. The crest twirls as a transition mask between windows/clips when logoBetween is on.
export default function HeroPlaylist({ clips = [], poster, logoBetween = true }) {
  // Only keep clips we can actually play: MP4 needs a source, YouTube needs a parseable id.
  const list = clips.filter((c) => {
    if (!c || !c.source) return false;
    if (c.type === "youtube") return !!extractYouTubeId(c.source);
    return true;
  });
  const [idx, setIdx] = useState(0);
  const [masking, setMasking] = useState(logoBetween);
  const startedRef = useRef(false);

  const single = list.length === 1;
  const clip = list.length ? list[idx % list.length] : null;

  const done = () => {
    if (list.length < 2) return;
    if (logoBetween) setMasking(true);
    setTimeout(() => setIdx((i) => (i + 1) % list.length), logoBetween ? 650 : 0);
  };
  const ready = () => {
    startedRef.current = true;
    setTimeout(() => setMasking(false), logoBetween ? 350 : 0);
  };
  // Twirl between loop windows of the same clip; auto-unmask in case PLAYING doesn't re-fire.
  const seek = () => {
    if (!logoBetween) return;
    setMasking(true);
    setTimeout(() => setMasking(false), 900);
  };
  // A clip that errors or never starts must not freeze the header — skip past it.
  const skip = () => {
    setMasking(false);
    if (list.length > 1) setIdx((i) => (i + 1) % list.length);
  };

  // Watchdog: if the current clip never starts playing within 9s, move on.
  useEffect(() => {
    startedRef.current = false;
    const t = setTimeout(() => { if (!startedRef.current) skip(); }, 9000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, list.length]);

  if (!clip) {
    return poster ? (
      <div className="absolute inset-0"><img src={poster} alt="" className="w-full h-full object-cover" /></div>
    ) : null;
  }

  const isYT = clip.type === "youtube";
  const ytId = isYT ? extractYouTubeId(clip.source) : "";

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" data-testid="hero-playlist">
      {poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />}

      {isYT && ytId ? (
        <YouTubeClip
          key={`yt-${idx}-${ytId}`}
          videoId={ytId}
          loops={clip.loops || []}
          single={single}
          onReady={ready}
          onSeek={seek}
          onDone={done}
          onError={skip}
        />
      ) : (
        <video
          key={`mp4-${idx}-${clip.source}`}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          playsInline
          loop={single}
          onCanPlay={ready}
          onEnded={single ? undefined : done}
          onError={skip}
        >
          <source src={clip.source} type="video/mp4" />
        </video>
      )}

      {logoBetween && (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-black transition-opacity duration-700 ${
            masking ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={club.crest}
            alt="Atlético South Florida crest"
            className={`w-44 h-44 object-contain drop-shadow-[0_4px_30px_rgba(229,184,51,0.6)] ${masking ? "logo-twirl" : ""}`}
          />
        </div>
      )}
    </div>
  );
}
